import type { Field } from "@/lib/admin/schema";
import type { PageBlock, SitePage } from "@/lib/content/types";

/* The page builder's vocabulary.

   A page is an address and an ordered list of blocks. Every block type here is
   a component the site already draws somewhere — the lobby's sections one by
   one, and each other page's body as a whole — plus three that exist only for
   the builder: a page header, rich text and raw HTML. Any block can go on any
   page; this file says what exists and what each one can be told, and
   components/pages/PageRenderer.tsx says how each one is drawn.

   Client-safe on purpose: the admin's editor reads it to offer the picker and
   the per-block fields, and the server's coercion reads it to keep saved
   pages to known types. */

export const BLOCK_ORIGINS = [
  "Homepage",
  "Casino",
  "Promotions",
  "Leaderboards",
  "Rewards",
  "Studios",
  "Blog",
  "Missions",
  "Custom",
] as const;

export type BlockOrigin = (typeof BLOCK_ORIGINS)[number];

export type BlockDefinition = {
  type: string;
  label: string;
  origin: BlockOrigin;
  blurb: string;
  /* `contained` blocks share the site's 1144px column with their neighbours;
     `bleed` blocks span the page and bring their own layout. */
  layout: "bleed" | "contained";
  fields: Field[];
  blank: Record<string, unknown>;
  /* The admin section whose content this block draws — the hero carousel
     draws Homepage → Hero slides — so the page builder can open that
     section's fields right under the block. Blocks that show platform data
     have none. */
  content?: string;
};

const plain = (
  type: string,
  label: string,
  origin: BlockOrigin,
  blurb: string,
  layout: BlockDefinition["layout"] = "contained",
  content?: string,
): BlockDefinition => ({ type, label, origin, blurb, layout, fields: [], blank: {}, content });

const WIDTHS = ["Narrow", "Wide"];

/* Every content-drawing block carries its own copy: the shared Homepage
   sections are gone, and each instance of a block on each page is configured
   on the block itself (translations too, per block via its locales). */
/* The kit's two hero frames: the homepage's 600px and the casino lobby's
   400px. "Scene" is a tall illustration with the copy at the top, whose
   lower part the next blocks rise over (Nebula's, after duel.com). "Peek"
   is a row of wide banners, the current one centred with its neighbours
   showing, dimmed, at both sides (Aldebaran's, after Topzinho). */
export const HERO_SIZES = ["Large", "Small", "Scene", "Headline", "Offer", "Peek"] as const;

/* What sits beside the hero's slides on the right: nothing, or a sign-up
   panel — a title, selling points, an e-mail field and a button that opens
   the sign-in with the address filled in (Hugo Casino's registration form).
   Signed-in players never see it. */
export const HERO_PANELS = ["None", "Sign-up"] as const;

const heroSlideFields: Field[] = [
  { kind: "text", key: "title", label: "Title" },
  { kind: "textarea", key: "body", label: "Body", rows: 2 },
  { kind: "text", key: "cta", label: "Button label" },
  { kind: "text", key: "href", label: "Button link" },
  { kind: "image", key: "imageUrl", label: "Image" },
  { kind: "text", key: "outline", label: "Outlined line", hint: "Offer height only: a line under the title in outlined type." },
];

const promoCardFields: Field[] = [
  { kind: "text", key: "tag", label: "Tag" },
  { kind: "text", key: "title", label: "Title" },
  { kind: "textarea", key: "body", label: "Body", rows: 2 },
  { kind: "text", key: "cta", label: "Button label" },
  { kind: "text", key: "href", label: "Button link" },
  { kind: "image", key: "imageUrl", label: "Image" },
];

const uspFields: Field[] = [
  { kind: "select", key: "icon", label: "Icon", options: ["cashout", "chat", "eye"] },
  { kind: "text", key: "title", label: "Title" },
  { kind: "textarea", key: "body", label: "Body", rows: 2 },
];

const faqFields: Field[] = [
  { kind: "text", key: "q", label: "Question" },
  { kind: "textarea", key: "a", label: "Answer", rows: 3 },
];

/* The rows a game browser or a casino lobby lays out, one field shared by
   both so the two blocks are told the same thing the same way. */
const CATEGORY_ROWS: Field = {
  kind: "list",
  key: "rows",
  label: "Category rows",
  itemLabel: "Row",
  titleKey: "category",
  blank: { category: "", enlarged: false, ranked: false },
  max: 12,
  fields: [
    { kind: "category", key: "category", label: "Category" },
    {
      kind: "boolean",
      key: "enlarged",
      label: "Enlarge the first game tile",
      hint: "One large tile beside two rows of games",
    },
    {
      kind: "boolean",
      key: "ranked",
      label: "Numbered top 10",
      hint: "The category's first ten games in one row, each beside its rank in large outlined figures",
    },
  ],
};

const GAMES_PER_ROW: Field = { kind: "number", key: "gamesPerRow", label: "Games per row", min: 4, max: 28 };

export const SEARCH_BAR_OPTIONS = ["Below the pills", "Above the pills", "Hidden"] as const;
export const SECTION_OPTIONS = ["Panels", "Plain"] as const;
export const TILE_OPTIONS = ["Standard", "Small"] as const;

/* How a game browser is told to look. The homepage's browser is the
   defaults; the casino lobby page sets every one of these the way the
   kit's desktop-casino-lobby draws it. */
export type GameBrowserSettings = {
  rows: GameBrowserRow[];
  categoryRows: number;
  gamesPerRow: number;
  searchBar: (typeof SEARCH_BAR_OPTIONS)[number];
  categoryCards: boolean;
  sections: (typeof SECTION_OPTIONS)[number];
  tiles: (typeof TILE_OPTIONS)[number];
};

const pick = <T extends readonly string[]>(options: T, value: unknown): T[number] =>
  typeof value === "string" && options.includes(value) ? value : options[0];

export const gameBrowserSettings = (props: Record<string, unknown>): GameBrowserSettings => ({
  rows: gameBrowserRows(props),
  categoryRows: typeof props.categoryRows === "number" ? props.categoryRows : 3,
  gamesPerRow: typeof props.gamesPerRow === "number" ? props.gamesPerRow : 14,
  searchBar: pick(SEARCH_BAR_OPTIONS, props.searchBar),
  categoryCards: props.categoryCards === true,
  sections: pick(SECTION_OPTIONS, props.sections),
  tiles: pick(TILE_OPTIONS, props.tiles),
});

/* The kit's casino lobby, as the shipped /casino page tells its browser. */
export const LOBBY_BROWSER: GameBrowserSettings = {
  rows: [],
  categoryRows: 12,
  gamesPerRow: 14,
  searchBar: "Above the pills",
  categoryCards: true,
  sections: "Plain",
  tiles: "Small",
};

/* The kit's category page (desktop-category-page / mobile-category-page) is
   two components under the small hero: the category pills, and the
   category's games — title and count, search bar, provider selector, and a
   grid that pages with "Load more". */
export type CategoryPillsSettings = { lobby: boolean };

export const categoryPillsSettings = (props: Record<string, unknown>): CategoryPillsSettings => ({
  lobby: props.lobby !== false,
});

export const CATEGORY_PILLS: CategoryPillsSettings = { lobby: true };

export type CategoryGamesSettings = {
  /* A category slug; blank is the category in the address (/c/…), or every
     game on a page that has none. */
  category: string;
  search: boolean;
  providers: boolean;
  pageSize: number;
};

export const categoryGamesSettings = (props: Record<string, unknown>): CategoryGamesSettings => ({
  category: typeof props.category === "string" ? props.category.trim() : "",
  search: props.search !== false,
  providers: props.providers !== false,
  pageSize: typeof props.pageSize === "number" ? Math.min(96, Math.max(8, Math.round(props.pageSize))) : 32,
});

/* Four rows of eight, as the kit draws the desktop grid. */
export const CATEGORY_GAMES: CategoryGamesSettings = { category: "", search: true, providers: true, pageSize: 32 };

export const BLOCKS: BlockDefinition[] = [
  {
    type: "hero",
    label: "Hero carousel",
    origin: "Homepage",
    blurb:
      "The top carousel; its slides live on this block. Large is the homepage's frame, small the casino lobby's, Scene a tall illustration with the copy at the top that the next blocks rise over, Headline a text-first hero — a huge headline in the brand gradient over the page, the image as art on the right; Offer the headline left with an outlined line, ticked points and a second link, and on the right the image as art over tilted cards, floating chips and an offer card; with the copy off the slides are banners that link where their button would. Hidden while the hero flag is off.",
    layout: "bleed",
    fields: [
      { kind: "select", key: "size", label: "Height", options: [...HERO_SIZES] },
      {
        kind: "boolean",
        key: "copy",
        label: "Copy over the image",
        hint: "Each slide's title, text and button; off, the slide is a banner",
      },
      {
        kind: "list",
        key: "slides",
        label: "Slides",
        itemLabel: "Slide",
        titleKey: "title",
        blank: { title: "New slide", body: "", cta: "Play now", href: "/c/all", imageUrl: "" },
        fields: heroSlideFields,
      },
      {
        kind: "select",
        key: "panel",
        label: "Beside the slides",
        options: [...HERO_PANELS],
        hint: "Sign-up: a registration panel on the right, over the slides on desktop and under them on a phone. Hidden once signed in.",
      },
      { kind: "text", key: "panelTitle", label: "Sign-up panel title" },
      { kind: "textarea", key: "panelBody", label: "Sign-up panel text", rows: 2 },
      {
        kind: "textarea",
        key: "panelPoints",
        label: "Sign-up panel selling points",
        rows: 3,
        hint: "One per line, each with a tick.",
      },
      { kind: "text", key: "panelCta", label: "Sign-up panel button" },
      { kind: "text", key: "eyebrow", label: "Offer hero: label over the title", hint: "Offer height only, as are the fields below." },
      { kind: "textarea", key: "points", label: "Offer hero: selling points", rows: 3, hint: "One per line, each with a tick, under the buttons." },
      { kind: "text", key: "linkLabel", label: "Offer hero: second link" },
      { kind: "text", key: "linkHref", label: "Offer hero: second link address" },
      {
        kind: "textarea",
        key: "artCards",
        label: "Offer hero: art cards",
        rows: 3,
        hint: "Up to three image addresses, one per line, tilted behind the slide's image.",
      },
      { kind: "textarea", key: "chips", label: "Offer hero: floating chips", rows: 2, hint: "Up to two short labels over the art, one per line." },
      { kind: "text", key: "offerLabel", label: "Offer card label" },
      { kind: "text", key: "offerTag", label: "Offer card tag", hint: "A short word beside the label with a live dot, such as Live." },
      { kind: "text", key: "offerKicker", label: "Offer card line over the amount" },
      { kind: "text", key: "offerAmount", label: "Offer card amount" },
      { kind: "text", key: "offerExtra", label: "Offer card line under the amount" },
      { kind: "text", key: "offerCta", label: "Offer card button", hint: "Opens the sign-in; signed in, it opens Promotions." },
      { kind: "text", key: "offerNote", label: "Offer card small print" },
    ],
    blank: { size: "Large", copy: true, slides: [], panel: "None" },
  },
  {
    type: "usps",
    label: "USP row",
    origin: "Homepage",
    blurb: "A row of selling points, written on this block. Strip shows them as figures in a ruled band: each title large, its text a small label.",
    layout: "contained",
    fields: [
      {
        kind: "select",
        key: "style",
        label: "Style",
        options: ["Cards", "Strip"],
        hint: "Strip: each title as a large figure with its text as a small label, in a ruled band, the first with a live dot (Betmode's ticker).",
      },
      {
        kind: "list",
        key: "items",
        label: "Selling points",
        itemLabel: "Point",
        titleKey: "title",
        blank: { icon: "cashout", title: "New point", body: "" },
        fields: uspFields,
      },
    ],
    blank: { items: [] },
  },
  plain("liveWinsStrip", "Live wins strip", "Homepage", "Recent wins as a strip of cards. Hidden while live feeds are off."),
  {
    type: "promoCarousel",
    label: "Promo carousel",
    origin: "Homepage",
    blurb: "Designed offer cards as a carousel; the cards live on this block. Hidden while the promo carousel flag is off.",
    layout: "contained",
    fields: [
      {
        kind: "boolean",
        key: "copy",
        label: "Copy over the image",
        hint: "Each card's tag, title, text and button; off, the card is its image alone, linking where its button would",
      },
      {
        kind: "list",
        key: "cards",
        label: "Cards",
        itemLabel: "Card",
        titleKey: "title",
        blank: { tag: "NEW", title: "New offer", body: "", cta: "Read more", href: "/promotions", imageUrl: "" },
        fields: promoCardFields,
      },
    ],
    blank: { copy: true, cards: [] },
  },
  plain("bigWins", "Big wins", "Homepage", "The biggest multipliers settled recently. Hidden while live feeds are off."),
  plain("bankroll", "Bankroll", "Homepage", "The house bankroll tiles, read live from chain. Hidden while the bankroll flag is off."),
  plain("liveWinsTicker", "Live wins ticker", "Homepage", "Recent wins as a scrolling ticker. Hidden while live feeds are off."),
  {
    type: "gameBrowser",
    label: "Game browser",
    origin: "Homepage",
    blurb:
      "The search bar, the category pills and the game rows. Pick which categories get a row and whether a row opens with one large game tile beside two rows of tiles, as the kit draws it; with no rows picked, the first categories the platform lists are shown. The homepage draws it as panels on the standard tile; the Casino page as the kit's lobby — search bar first, plain sections on the small tile.",
    layout: "contained",
    fields: [
      CATEGORY_ROWS,
      { kind: "number", key: "categoryRows", label: "Automatic rows, when none are picked above", min: 1, max: 12 },
      GAMES_PER_ROW,
      { kind: "select", key: "searchBar", label: "Search bar", options: [...SEARCH_BAR_OPTIONS] },
      {
        kind: "boolean",
        key: "categoryCards",
        label: "Category cards under the pills",
        hint: "A card for every category with its game count, replaced by the results while searching",
      },
      { kind: "select", key: "sections", label: "Rows drawn as", options: [...SECTION_OPTIONS] },
      { kind: "select", key: "tiles", label: "Tile size", options: [...TILE_OPTIONS] },
    ],
    blank: {
      rows: [],
      categoryRows: 3,
      gamesPerRow: 14,
      searchBar: "Below the pills",
      categoryCards: false,
      sections: "Panels",
      tiles: "Standard",
    },
  },
  plain(
    "categoryCards",
    "Category cards",
    "Casino",
    "A card for every category the platform publishes, with its icon, name and game count, opening the category's page.",
  ),
  {
    type: "categoryPills",
    label: "Category pills",
    origin: "Casino",
    blurb:
      "The row of category pills, each opening that category's page, with the page you are on lit — the kit's nav-tab-pill, as the category page opens with it.",
    layout: "contained",
    fields: [{ kind: "boolean", key: "lobby", label: "Lobby pill first", hint: "Opens the casino lobby" }],
    blank: { ...CATEGORY_PILLS },
  },
  {
    type: "categoryGames",
    label: "Category games",
    origin: "Casino",
    blurb:
      "One category's games as the kit's category page draws them: the title with its game count, the search bar and provider selector, and the game grid with Load more. On the category page it shows the category in the address.",
    layout: "contained",
    fields: [
      {
        kind: "category",
        key: "category",
        label: "Category",
        hint: "Leave unset on the category page, where it follows the address; elsewhere unset shows every game",
      },
      { kind: "boolean", key: "search", label: "Search bar" },
      { kind: "boolean", key: "providers", label: "Provider selector" },
      { kind: "number", key: "pageSize", label: "Games per page", min: 8, max: 96 },
    ],
    blank: { ...CATEGORY_GAMES },
  },
  plain("studioStrip", "Studio strip", "Homepage", "The strip of studio names, read from the platform."),
  plain("liveStats", "Live stats", "Homepage", "The live bets table with player cards. Hidden while live feeds are off."),
  {
    type: "contentBlock",
    label: "Why us block",
    origin: "Homepage",
    blurb: "A headline, body and stat row, written on this block.",
    layout: "contained",
    fields: [
      { kind: "text", key: "eyebrow", label: "Eyebrow" },
      { kind: "text", key: "headline", label: "Headline" },
      { kind: "textarea", key: "body", label: "Body", rows: 4 },
      { kind: "text", key: "kicker", label: "Kicker" },
      {
        kind: "list",
        key: "stats",
        label: "Stats",
        itemLabel: "Stat",
        titleKey: "k",
        blank: { k: "0", v: "New stat" },
        fields: [
          { kind: "text", key: "k", label: "Figure" },
          { kind: "text", key: "v", label: "Label" },
        ],
      },
    ],
    blank: { eyebrow: "", headline: "New block", body: "", kicker: "", stats: [] },
  },
  {
    type: "faq",
    label: "FAQ",
    origin: "Homepage",
    blurb: "Questions and answers, written on this block.",
    layout: "contained",
    fields: [
      {
        kind: "list",
        key: "items",
        label: "Questions",
        itemLabel: "Question",
        titleKey: "q",
        blank: { q: "New question", a: "" },
        fields: faqFields,
      },
    ],
    blank: { items: [] },
  },
  plain("promotionsList", "Promotions list", "Promotions", "Every promotion on the platform, running ones first, finished ones after.", "bleed"),
  {
    type: "promoCards",
    label: "Offer cards",
    origin: "Promotions",
    blurb: "Designed offer cards, one full-width card each; the cards live on this block.",
    layout: "bleed",
    fields: [
      {
        kind: "list",
        key: "cards",
        label: "Cards",
        itemLabel: "Card",
        titleKey: "title",
        blank: { tag: "NEW", title: "New offer", body: "", cta: "Read more", href: "/promotions", imageUrl: "" },
        fields: promoCardFields,
      },
    ],
    blank: { cards: [] },
  },
  {
    type: "leaderboards",
    label: "Leaderboards",
    origin: "Leaderboards",
    blurb: "The race header, the race tabs, standings, qualifying bets and the volume board. Brings its own header, so it needs no Page header above it.",
    layout: "bleed",
    fields: [
      { kind: "text", key: "title", label: "Title", hint: "Shown when no race is selected; a race shows its own name." },
      { kind: "textarea", key: "body", label: "Text", rows: 2 },
    ],
    blank: { title: "Leaderboards", body: "" },
  },
  plain("rewards", "Rewards", "Rewards", "Available rewards, the player's own rewards and their reward history."),
  {
    type: "studiosGrid",
    label: "Studios grid",
    origin: "Studios",
    blurb: "Every studio on the site, the names curated on this block first.",
    layout: "bleed",
    fields: [{ kind: "tags", key: "curated", label: "Curated first", hint: "Studio names to lead the grid, in order." }],
    blank: { curated: [] },
  },
  plain("blogList", "Blog posts", "Blog", "One card per page whose address lives under blog/ — each post is a page of its own.", "bleed", "pages"),
  plain("missionBoard", "Mission board", "Missions", "Every mission and the player's progress on it.", "contained", "missions"),
  {
    type: "pageHeader",
    label: "Page header",
    origin: "Custom",
    blurb: "The tall band with the page's title and text, the way every content page opens.",
    layout: "bleed",
    fields: [
      { kind: "text", key: "title", label: "Title" },
      { kind: "textarea", key: "body", label: "Text", rows: 3 },
      { kind: "image", key: "image", label: "Image", hint: "Optional. Fills the band behind the title." },
    ],
    blank: { title: "New page", body: "", image: "" },
  },
  {
    type: "document",
    label: "Document",
    origin: "Custom",
    blurb: "A long-form document: the tall header and a grid of heading + paragraph cards — the shape of every legal, support, about and blog page.",
    layout: "bleed",
    fields: [
      { kind: "text", key: "title", label: "Title" },
      { kind: "textarea", key: "intro", label: "Intro", rows: 3 },
      {
        kind: "list",
        key: "sections",
        label: "Sections",
        itemLabel: "Section",
        titleKey: "h",
        blank: { h: "New section", p: "" },
        fields: [
          { kind: "text", key: "h", label: "Heading" },
          { kind: "textarea", key: "p", label: "Body", rows: 4 },
        ],
      },
    ],
    blank: { title: "New document", intro: "", sections: [] },
  },
  {
    type: "richText",
    label: "Rich text",
    origin: "Custom",
    blurb: "Headings, paragraphs, links, lists and images, written as text with a toolbar.",
    layout: "contained",
    fields: [
      { kind: "richtext", key: "markdown", label: "Content" },
      { kind: "select", key: "width", label: "Width", options: WIDTHS },
    ],
    blank: {
      markdown:
        "## A heading\n\nWrite here. **Bold**, *italics*, [links](https://example.com), lists and images all work — use the toolbar, or type the marks.",
      width: "Narrow",
    },
  },
  {
    type: "html",
    label: "Custom HTML",
    origin: "Custom",
    blurb: "Your own HTML, rendered on the page exactly as written.",
    layout: "contained",
    fields: [
      {
        kind: "code",
        key: "html",
        label: "HTML",
        rows: 14,
        hint: "Rendered as-is, scripts and all — only paste HTML you trust.",
      },
      { kind: "select", key: "width", label: "Width", options: WIDTHS },
    ],
    blank: { html: "<div>\n  <h2>Custom block</h2>\n  <p>Anything goes here.</p>\n</div>", width: "Wide" },
  },
];

export const blockDefinition = (type: string) => BLOCKS.find((block) => block.type === type);

/* How a hero block draws: the kit's large or small frame, and whether the
   slides carry their copy. Nothing set is the homepage's hero. */
export type HeroPanel = { title: string; body: string; points: string[]; cta: string };

/* The Offer hero's furniture around its slides (Capella's, after Betmode). */
export type HeroOffer = {
  eyebrow: string;
  points: string[];
  link: { label: string; href: string } | null;
  artCards: string[];
  chips: string[];
  card: { label: string; tag: string; kicker: string; amount: string; extra: string; cta: string; note: string } | null;
};

const words = (value: unknown, max: number) => (typeof value === "string" ? value.trim().slice(0, max) : "");

const lines = (value: unknown, max: number, count: number) =>
  words(value, max)
    .split(/\r?\n/)
    .map((line) => line.trim())
    .filter(Boolean)
    .slice(0, count);

const heroOffer = (props: Record<string, unknown>): HeroOffer => {
  const linkLabel = words(props.linkLabel, 40);
  const card = {
    label: words(props.offerLabel, 40),
    tag: words(props.offerTag, 16),
    kicker: words(props.offerKicker, 40),
    amount: words(props.offerAmount, 24),
    extra: words(props.offerExtra, 60),
    cta: words(props.offerCta, 40),
    note: words(props.offerNote, 160),
  };

  return {
    eyebrow: words(props.eyebrow, 60),
    points: lines(props.points, 400, 4),
    link: linkLabel ? { label: linkLabel, href: words(props.linkHref, 300) || "/casino" } : null,
    artCards: lines(props.artCards, 1500, 3),
    chips: lines(props.chips, 120, 2),
    card: card.label || card.amount ? card : null,
  };
};

export const heroSettings = (props: Record<string, unknown>) => ({
  size:
    props.size === "Small"
      ? ("small" as const)
      : props.size === "Scene"
        ? ("scene" as const)
        : props.size === "Headline"
          ? ("headline" as const)
          : props.size === "Offer"
            ? ("offer" as const)
            : props.size === "Peek"
              ? ("peek" as const)
              : ("large" as const),
  copy: props.copy !== false,
  panel:
    props.panel === "Sign-up"
      ? ({
          title: words(props.panelTitle, 80),
          body: words(props.panelBody, 240),
          points: words(props.panelPoints, 600)
            .split(/\r?\n/)
            .map((line) => line.trim())
            .filter(Boolean)
            .slice(0, 5),
          cta: words(props.panelCta, 40),
        } satisfies HeroPanel)
      : null,
  ...(props.size === "Offer" ? { offer: heroOffer(props) } : {}),
});

export type GameBrowserRow = { category: string; enlarged: boolean; ranked: boolean };

/* The game browser's chosen rows, from its settings: only rows naming a
   category, in the order given. Empty means the automatic rows. */
export const gameBrowserRows = (props: Record<string, unknown>): GameBrowserRow[] => {
  if (!Array.isArray(props.rows)) return [];

  return props.rows.flatMap((row) => {
    const source = (row ?? {}) as Record<string, unknown>;
    const category = typeof source.category === "string" ? source.category.trim() : "";

    return category ? [{ category, enlarged: source.enlarged === true, ranked: source.ranked === true }] : [];
  });
};

/* The pages the site ships with. Each has a route file of its own, which is
   why their slugs are fixed by default: the file answers at that address, and
   redirects to the new one when an operator moves the page. The homepage
   cannot move — it is "/" — and neither can the category page: it is not one
   address but the layout every /c/<category> is drawn with. */
export const BUILTIN_PAGES = {
  home: { slug: "", title: "Home" },
  casino: { slug: "casino", title: "Casino" },
  category: { slug: "c", title: "Category page" },
  promotions: { slug: "promotions", title: "Promotions" },
  leaderboards: { slug: "leaderboards", title: "Leaderboards" },
  rewards: { slug: "rewards", title: "Rewards" },
  studios: { slug: "studios", title: "Game Studios" },
  blog: { slug: "blog", title: "Blog" },
  missions: { slug: "missions", title: "Missions" },
} as const;

export type BuiltinPageId = keyof typeof BUILTIN_PAGES;

export const isBuiltinPageId = (id: string): id is BuiltinPageId => Object.hasOwn(BUILTIN_PAGES, id);

/* The shipped pages whose address cannot be edited. */
export const hasFixedSlug = (id: string) => id === "home" || id === "category";

/* Where a page answers, as the admin shows it. */
export const pageAddress = (page: Pick<SitePage, "id" | "slug">) =>
  page.id === "category" ? "/c/<category>" : page.slug ? `/${page.slug}` : "/";

/* First path segments the app itself answers on, dynamic children included —
   /promotions/[id] is a route file, so nothing custom can live under it. A
   custom page cannot take one: the route file would win and the page would
   never be reachable. The shipped pages' own slugs are reserved exactly —
   /blog belongs to the blog page — but their sub-paths are free, which is
   where the document pages live (legal/…, support/…, about/…, blog/…). */
export const RESERVED_SEGMENTS = new Set(["admin", "api", "account", "auth", "c", "game", "join", "promotions", "sports", "_next"]);

const RESERVED_EXACT = new Set(
  Object.values(BUILTIN_PAGES)
    .map((page) => page.slug as string)
    .filter(Boolean),
);

export const normalizeSlug = (raw: string) =>
  raw
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9/-]+/g, "-")
    .replace(/-+/g, "-")
    .split("/")
    .map((segment) => segment.replace(/^-+|-+$/g, ""))
    .filter(Boolean)
    .join("/");

export const isReservedSlug = (slug: string) => RESERVED_SEGMENTS.has(slug.split("/")[0] ?? "") || RESERVED_EXACT.has(slug);

/* Why a page's slug cannot be saved as typed, or null when it can. */
export const slugProblem = (page: SitePage, pages: SitePage[]): string | null => {
  if (hasFixedSlug(page.id)) return null;

  const slug = normalizeSlug(page.slug);
  const own = isBuiltinPageId(page.id) ? BUILTIN_PAGES[page.id].slug : null;

  if (slug === "") return "Give the page a slug.";
  if (isReservedSlug(slug) && slug !== own) return "That path belongs to the site itself. Pick another.";
  if (pages.some((other) => other.id !== page.id && normalizeSlug(other.slug) === slug)) {
    return "Another page already uses this slug.";
  }

  return null;
};

export const randomId = () => Math.random().toString(36).slice(2, 10);

export const newBlock = (type: string): PageBlock => {
  const definition = blockDefinition(type);

  return { id: `${type}-${randomId()}`, type, props: { ...(definition?.blank ?? {}) } };
};

export const newPage = (existing: SitePage[]): SitePage => {
  const taken = new Set(existing.map((page) => page.slug));
  let slug = "new-page";

  for (let n = 2; taken.has(slug); n += 1) slug = `new-page-${n}`;

  return {
    id: `page-${randomId()}`,
    slug,
    title: "New page",
    description: "",
    blocks: [newBlock("pageHeader"), newBlock("richText")],
  };
};
